import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  Apple,
  Android,
  Pets,
  MedicalServices,
  DeviceUnknown,
  Visibility,
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

interface MobileUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  userType: 'owner' | 'sitter' | null;
  roles?: ('owner' | 'sitter')[];
  activeRole?: 'owner' | 'sitter' | null;
  platform: 'ios' | 'android' | null;
  approvalStatus?: string;
  serviceType?: 'sitter' | 'groomer' | 'vet' | 'trainer' | null;
  lastLogin?: string;
  createdAt: string;
  isActive: boolean;
  country?: string | null;
  petCount?: number;
  language?: 'en' | 'de';
}

const MobileUsersList: React.FC = () => {
  const [users, setUsers] = useState<MobileUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0=all, 1=ios, 2=android
  const [selectedUser, setSelectedUser] = useState<MobileUser | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/mobile-users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch mobile users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = tab === 0
    ? users
    : tab === 1
      ? users.filter(u => u.platform === 'ios')
      : users.filter(u => u.platform === 'android');

  const iosCount = users.filter(u => u.platform === 'ios').length;
  const androidCount = users.filter(u => u.platform === 'android').length;
  const unknownCount = users.filter(u => !u.platform).length;

  const getPlatformIcon = (platform: string | null) => {
    switch (platform) {
      case 'ios': return <Apple sx={{ fontSize: 18 }} />;
      case 'android': return <Android sx={{ fontSize: 18 }} />;
      default: return <DeviceUnknown sx={{ fontSize: 18 }} />;
    }
  };

  const getPlatformLabel = (platform: string | null) => {
    switch (platform) {
      case 'ios': return 'iOS';
      case 'android': return 'Android';
      default: return 'Unknown';
    }
  };

  const countryNames: Record<string, string> = {
    DK: 'Denmark', EE: 'Estonia', FI: 'Finland', FR: 'France', DE: 'Germany',
    GI: 'Gibraltar', GR: 'Greece', HU: 'Hungary', IE: 'Ireland', IT: 'Italy',
    LV: 'Latvia', LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg',
    MT: 'Malta', NL: 'Netherlands', NO: 'Norway', PL: 'Poland', PT: 'Portugal',
    RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia', ES: 'Spain', SE: 'Sweden',
    CH: 'Switzerland', GB: 'United Kingdom',
  };

  const serviceTypeLabels: Record<string, string> = {
    sitter: 'Pet Sitter',
    groomer: 'Pet Groomer',
    vet: 'Veterinarian',
    trainer: 'Pet Trainer',
  };

  const getProviderChip = (user: MobileUser) => {
    if (user.approvalStatus === 'approved' && user.serviceType) {
      return <Chip label={serviceTypeLabels[user.serviceType] || user.serviceType} size="small" color="success" icon={<MedicalServices />} />;
    }
    if (user.approvalStatus === 'rejected') {
      return <Chip label="Provider (Rejected)" size="small" color="error" icon={<MedicalServices />} />;
    }
    return <Chip label="Provider (Pending)" size="small" color="warning" icon={<MedicalServices />} />;
  };

  const getUserTypeChip = (user: MobileUser) => {
    const roles = user.roles && user.roles.length > 0 ? user.roles : (user.userType ? [user.userType] : []);
    const isDualRole = roles.includes('owner') && roles.includes('sitter');

    if (roles.length === 0) return <Chip label="Not set" size="small" />;

    if (isDualRole) {
      return (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          <Chip label="Pet Owner" size="small" color="primary" icon={<Pets />} />
          {getProviderChip(user)}
          {user.activeRole && (
            <Chip
              label={`Active: ${user.activeRole === 'owner' ? 'Owner' : 'Provider'}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
      );
    }

    if (roles.includes('owner')) return <Chip label="Pet Owner" size="small" color="primary" icon={<Pets />} />;
    if (roles.includes('sitter')) return getProviderChip(user);
    return <Chip label={user.userType || 'Unknown'} size="small" />;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary chips */}
      <Box display="flex" gap={2} mb={2}>
        <Chip label={`Total: ${users.length}`} />
        <Chip icon={<Apple />} label={`iOS: ${iosCount}`} variant="outlined" />
        <Chip icon={<Android />} label={`Android: ${androidCount}`} color="success" variant="outlined" />
        {unknownCount > 0 && <Chip icon={<DeviceUnknown />} label={`Unknown: ${unknownCount}`} variant="outlined" />}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All Users (${users.length})`} />
        <Tab label={`iOS (${iosCount})`} icon={<Apple />} iconPosition="start" />
        <Tab label={`Android (${androidCount})`} icon={<Android />} iconPosition="start" />
      </Tabs>

      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: user.platform === 'ios' ? '#000' : '#3DDC84', width: 36, height: 36 }}>
                      {user.firstName?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip
                    icon={getPlatformIcon(user.platform)}
                    label={getPlatformLabel(user.platform)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: user.platform === 'ios' ? '#000' : user.platform === 'android' ? '#3DDC84' : undefined,
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Chip size="small" variant="outlined" label={user.language === 'de' ? 'Deutsch' : 'English'} color={user.language === 'de' ? 'info' : 'default'} />
                </TableCell>

                <TableCell>
                  {getUserTypeChip(user)}
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {user.phoneNumber || '-'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.createdAt)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.lastLogin)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={user.isActive ? 'success' : 'default'}
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton size="small" onClick={() => setSelectedUser(user)} title="View profile">
                    <Visibility fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No users found
          </Typography>
        </Box>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onClose={() => setSelectedUser(null)} maxWidth="sm" fullWidth>
        {selectedUser && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: selectedUser.platform === 'ios' ? '#000' : '#3DDC84', width: 48, height: 48 }}>
                  {selectedUser.firstName?.charAt(0) || '?'}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUser.firstName} {selectedUser.lastName}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Divider sx={{ mb: 2 }} />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">User Type</Typography>
                  <Box mt={0.5}>{getUserTypeChip(selectedUser)}</Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Platform</Typography>
                  <Box mt={0.5}>
                    <Chip
                      icon={getPlatformIcon(selectedUser.platform)}
                      label={getPlatformLabel(selectedUser.platform)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Language</Typography>
                  <Box mt={0.5}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={selectedUser.language === 'de' ? 'Deutsch' : 'English'}
                      color={selectedUser.language === 'de' ? 'info' : 'default'}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Country</Typography>
                  <Typography variant="body2">
                    {selectedUser.country ? (countryNames[selectedUser.country] || selectedUser.country) : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                  <Typography variant="body2">{selectedUser.phoneNumber || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pets</Typography>
                  <Box mt={0.5}>
                    <Chip
                      icon={<Pets />}
                      label={selectedUser.petCount ? `${selectedUser.petCount} pet${selectedUser.petCount !== 1 ? 's' : ''}` : 'None'}
                      size="small"
                      variant="outlined"
                      color={selectedUser.petCount ? 'primary' : 'default'}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedUser.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={selectedUser.isActive ? 'success' : 'default'}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Registered</Typography>
                  <Typography variant="body2">{formatDate(selectedUser.createdAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Login</Typography>
                  <Typography variant="body2">{formatDate(selectedUser.lastLogin)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Roles</Typography>
                  <Box mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                    {(selectedUser.roles && selectedUser.roles.length > 0
                      ? selectedUser.roles
                      : selectedUser.userType ? [selectedUser.userType] : []
                    ).map(role => (
                      <Chip
                        key={role}
                        label={role === 'owner' ? 'Pet Owner' : 'Service Provider'}
                        size="small"
                        color={role === 'owner' ? 'primary' : 'secondary'}
                        icon={role === 'owner' ? <Pets /> : <MedicalServices />}
                        variant={role === selectedUser.activeRole ? 'filled' : 'outlined'}
                      />
                    ))}
                    {selectedUser.roles && selectedUser.roles.length > 1 && selectedUser.activeRole && (
                      <Chip
                        label={`Currently: ${selectedUser.activeRole === 'owner' ? 'Pet Owner' : 'Provider'}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}
                      />
                    )}
                  </Box>
                </Box>
                {selectedUser.roles?.includes('sitter') && selectedUser.approvalStatus && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Approval Status</Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={selectedUser.approvalStatus}
                        size="small"
                        color={
                          selectedUser.approvalStatus === 'approved' ? 'success' :
                          selectedUser.approvalStatus === 'rejected' ? 'error' : 'warning'
                        }
                      />
                    </Box>
                  </Box>
                )}
                {selectedUser.serviceType && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Type</Typography>
                    <Typography variant="body2">{serviceTypeLabels[selectedUser.serviceType] || selectedUser.serviceType}</Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedUser(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MobileUsersList;
